import { useEffect, useState } from "react";
import { Eye, ExternalLink, Loader2 } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { shortDate, money } from "@/lib/invoice";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import InvoiceDocument, { FitWidth } from "@/components/invoices/InvoiceDocument";

function PreviewDialog({ id, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setData(null); setError("");
    if (!id) return;
    Promise.all([api.get(`/invoices/${id}`), api.get("/invoice-settings")])
      .then(([i, s]) => setData({ inv: i.data, settings: s.data })).catch((e) => setError(formatError(e)));
  }, [id]);
  const inv = data?.inv;
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-card max-h-[90vh] overflow-y-auto" data-testid="skip-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Existing invoice {inv ? `#${inv.number}` : ""}</DialogTitle>
          <DialogDescription data-testid="skip-preview-summary">
            {inv ? `${inv.customer?.name || "—"} · ${shortDate(inv.service_date)} · ${inv.status} · ${money(inv.total)}` : "This is the invoice that caused the skip."}
          </DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm text-red-400" data-testid="skip-preview-error">{error}</p>}
        {!data && !error && <Loader2 className="animate-spin text-primary" />}
        {inv && <>
          <div className="rounded-md bg-slate-200 p-3"><FitWidth><InvoiceDocument invoice={inv} settings={data.settings} /></FitWidth></div>
          <Button variant="outline" asChild className="w-fit"><a href={`/invoices/${inv.id}`} target="_blank" rel="noreferrer" data-testid="skip-preview-open"><ExternalLink size={14} /> Open to edit</a></Button>
        </>}
      </DialogContent>
    </Dialog>
  );
}

export default function SkipList({ skipped }) {
  const [view, setView] = useState(null);
  if (!skipped.length) return null;
  return (
    <>
      <ul className="max-h-48 overflow-y-auto border rounded-md divide-y text-xs text-muted-foreground" data-testid="skip-list">
        {skipped.map((s, i) => (
          <li key={i} className="flex items-center justify-between gap-2 px-3 py-1.5">
            <span><span className="font-mono">{s.number || "—"}</span> · {s.customer || "—"} · {s.reason}</span>
            {s.existing_id && <Button size="sm" variant="ghost" className="h-7" onClick={() => setView(s.existing_id)} data-testid={`skip-preview-${i}`}><Eye size={13} /> Preview</Button>}
          </li>
        ))}
      </ul>
      <PreviewDialog id={view} onClose={() => setView(null)} />
    </>
  );
}
